/**
 * 存储命名空间与安全存取。
 *
 * 两条来自生产事故的规则：
 *
 * 1. **前缀收口成一个来源**。localStorage 键、cookie 名、Cache 名全部从 Namespace 派生。
 *    散在七个文件里，改的时候必漏一个——而漏掉的那个恰恰最显眼（真事：改了存储前缀，
 *    Service Worker 里硬编码的那个 Cache 名没跟着改，冷启动深链写进了一个没人读的缓存，
 *    完全静默，只有真机点通知才会发现）。
 *
 * 2. **存储不可用时，失败要偏向可用，而不是偏向安全**。Safari 隐私模式、企业策略都会让
 *    storage 直接抛错。如果门禁写成"读不到就拦住"，那么写入同样会失败 → 这个人永远进不了
 *    自己的账号。少一层遮掩是遗憾，把用户锁在外面是故障。每个 catch 都要想清楚落在哪边。
 */
export function createNamespace(ns) {
    if (!/^[a-z][a-z0-9-]*$/i.test(ns)) {
        throw new Error(`[pwaship] 命名空间只能是字母开头的字母数字与连字符，收到 ${JSON.stringify(ns)}`);
    }
    return {
        ns,
        key: (name) => `${ns}.${name}`,
        cookie: (name) => `${ns}_${name}`,
        cache: (name) => `${ns}-${name}`,
    };
}
function resolveBackend(backend) {
    if (backend !== undefined)
        return backend;
    try {
        return typeof localStorage === 'undefined' ? null : localStorage;
    }
    catch {
        // 访问 localStorage 这个属性本身就可能抛（企业策略 / 禁用 cookie）
        return null;
    }
}
export function createSafeStorage(backend) {
    const store = resolveBackend(backend);
    return {
        get(key, fallback = null) {
            try {
                const v = store?.getItem(key);
                return v ?? fallback;
            }
            catch {
                return fallback;
            }
        },
        getJSON(key, fallback) {
            try {
                const raw = store?.getItem(key);
                if (raw == null)
                    return fallback;
                return JSON.parse(raw);
            }
            catch {
                // 值损坏（半截 JSON、被别的代码写脏）也走 fallback，不让启动路径炸掉
                return fallback;
            }
        },
        set(key, value) {
            try {
                store?.setItem(key, value);
                return store != null;
            }
            catch {
                // 配额满 / 隐私模式
                return false;
            }
        },
        setJSON(key, value) {
            try {
                store?.setItem(key, JSON.stringify(value));
                return store != null;
            }
            catch {
                return false;
            }
        },
        remove(key) {
            try {
                store?.removeItem(key);
                return store != null;
            }
            catch {
                return false;
            }
        },
        available() {
            if (!store)
                return false;
            const probe = '__pwaship_probe__';
            try {
                store.setItem(probe, '1');
                store.removeItem(probe);
                return true;
            }
            catch {
                return false;
            }
        },
    };
}
/**
 * 换命名空间时把存量键搬过来。
 *
 * ⚠️ 换前缀会**静默丢掉用户的既有设置**（应用锁、语言、主题、引导状态）。用户不会报
 * "设置丢了"，他会报"应用锁自动关了"——而你查不到任何错误日志。
 *
 * ⚠️ 迁移必须在**任何模块读这些键之前**跑。顺序错了，i18n / 应用锁会先读到空值，
 * 然后把默认值写回去，迁移就白做了。
 *
 * @returns 搬过来的键数量
 */
export function migrateLegacyKeys(ns, legacyNS, backend) {
    const store = resolveBackend(backend);
    if (!store || !legacyNS || legacyNS === ns)
        return 0;
    let moved = 0;
    try {
        const prefix = `${legacyNS}.`;
        // 先快照键名：边遍历边删会跳过元素
        const oldKeys = [];
        for (let i = 0; i < store.length; i += 1) {
            const k = store.key(i);
            if (k?.startsWith(prefix))
                oldKeys.push(k);
        }
        for (const oldKey of oldKeys) {
            const newKey = `${ns}.${oldKey.slice(prefix.length)}`;
            const value = store.getItem(oldKey);
            // 新键已有值就不覆盖：那是用户在新版本里改过的，比旧值新
            if (value != null && store.getItem(newKey) == null) {
                store.setItem(newKey, value);
                moved += 1;
            }
            store.removeItem(oldKey);
        }
    }
    catch {
        // 隐私模式下整个 localStorage 可能不可写。迁移失败不该阻断启动——
        // 代价是这些用户要重新设置一次，比白屏好。
    }
    return moved;
}
//# sourceMappingURL=storage.js.map